import { api } from './apiClient';
import type { CashEntry, CashEntryType } from '../utils/cashBook';

/** Livro Caixa no banco (antes ficava só no navegador de quem lançava). */
export function fetchCashEntries(from?: string, to?: string): Promise<CashEntry[]> {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = params.toString();
  return api.get<CashEntry[]>(`/cash-book${query ? `?${query}` : ''}`);
}

export type CreateCashEntryPayload = {
  type: CashEntryType;
  date: string;
  amount: number;
  category: string;
  description?: string;
  patientId?: string;
};

export function createCashEntry(payload: CreateCashEntryPayload): Promise<CashEntry> {
  return api.post<CashEntry>('/cash-book', payload);
}

/** Bloco #65: o lápis altera só o valor do lançamento. */
export function updateCashEntryAmount(id: string, amount: number): Promise<CashEntry> {
  return api.patch<CashEntry>(`/cash-book/${encodeURIComponent(id)}`, { amount });
}

/** Bloco #66: relatório financeiro com os filtros ativos (PDF/CSV; baixar ou enviar por e-mail). */
export type CashReportRequest = {
  content: 'consolidado' | 'entradas' | 'saidas';
  format: 'PDF' | 'CSV';
  delivery: 'download' | 'email';
  from?: string;
  to?: string;
  month?: string;
  patientId?: string;
  professionalId?: string;
  serviceName?: string;
};

export type CashReportDownload = { filename: string; contentType: string; base64: string };
export type CashReportSent = { sent: true; to: string; filename: string; bytes: number };

export function requestCashReport(payload: CashReportRequest): Promise<CashReportDownload | CashReportSent> {
  return api.post('/cash-book/report', payload);
}

export function deleteCashEntry(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/cash-book/${encodeURIComponent(id)}`);
}

/**
 * Bloco #55: "Compareceu" (`attended`) ou falta cobrada. A API usa o valor do
 * serviço do agendamento e não duplica; `entry: null` quando não há valor.
 */
export function recordAttendanceIncome(
  kind: 'agenda' | 'appointment',
  id: string,
  attended: boolean,
): Promise<{ entry: CashEntry | null; created: boolean; reason?: 'SEM_VALOR' }> {
  return api.post('/cash-book/attendance', { kind, id, attended });
}

/** Falta não cobrada: tira o lançamento do agendamento, se houver. */
export function removeAttendanceIncome(kind: 'agenda' | 'appointment', id: string): Promise<{ removed: number }> {
  return api.delete(`/cash-book/attendance/${kind}/${encodeURIComponent(id)}`);
}

/** Sobe os lançamentos que estavam no navegador (a API ignora repetidos). */
export function importCashEntries(entries: CashEntry[]): Promise<{ imported: number; skipped: number }> {
  const campos = entries.map((e) => ({
    id: e.id,
    type: e.type,
    date: e.date,
    amount: e.amount,
    category: e.category,
    description: e.description,
    patientId: e.patientId,
    patientName: e.patientName,
    appointmentId: e.appointmentId,
    professionalId: e.professionalId,
    professionalName: e.professionalName,
    serviceName: e.serviceName,
    source: e.source,
  }));
  return api.post('/cash-book/import', { entries: campos });
}
